<template>
  <div>
    <div class="d-flex flex-wrap mb-4">
      <!-- Search input -->
      <SearchInput
        ref="search-input-component"
        v-model="searchQuery"
        class="flex-grow-1"
        :placeholder="gettext('Search brands...')"
        @input="
          fetchBrandviewset({
            query: searchQuery,
            page: 1,
            labels: selectedLabels,
            certificates: selectedCertificates,
          })
        "
        @reset="onReset"
      />

      <!-- Label filter dropdown -->
      <FilterDropdown
        :label="gettext('Sustainability label')"
        :filter-count="selectedLabels.length"
        :options="$store.state.labels.results"
        class="mt-2 mt-sm-0 ms-sm-2"
        @select="onLabelSelect($event)"
      >
        <template #option="{ option }">
          <span class="d-flex align-items-center">
            <img
              :src="`/static/img/labels/${option.abbreviation}.png`"
              width="24"
              class="me-2"
            />
            <span class="flex-grow-1">{{ option.label_name }}</span>
            <IconCheckmark
              v-if="selectedLabels.includes(option.id)"
              class="flex-shrink-0"
            />
          </span>
        </template>
      </FilterDropdown>

      <!-- Certificate filter dropdown -->
      <FilterDropdown
        :label="gettext('Certificates')"
        :filter-count="selectedCertificates.length"
        :options="$store.state.certificates.results"
        class="certificate-dropdown mt-2 mt-sm-0 ms-sm-2"
        @select="onCertificateSelect($event)"
      >
        <template #option="{ option }">
          <span class="d-flex align-items-center">
            <span class="certificate-img me-1">
              <img :src="option.static_url" width="auto" />
            </span>
            <span class="flex-grow-1">{{ option.name }}</span>
            <IconCheckmark
              v-if="selectedCertificates.includes(option.id)"
              class="flex-shrink-0"
            />
          </span>
        </template>
      </FilterDropdown>
    </div>

    <!-- Tiles -->
    <div v-if="isLoading" class="tiles mb-4">
      <div class="my-4 mx-auto"><IconLoading /></div>
    </div>
    <div
      v-else-if="$store.state.viewset.results.length === 0"
      class="tiles mb-4"
    >
      <p class="lead py-4 mx-auto">
        {{
          gettext('Unfortunately there are no results for the current search')
        }}
      </p>
    </div>
    <div v-else class="tiles mb-4">
      <BrandTile
        v-for="brand in $store.state.viewset.results"
        :key="brand.id"
        :brand="brand"
        :labels-map="labelsMap"
        @open-modal="openBrandModal(brand)"
      />
    </div>

    <!-- Pagination -->
    <Pagination
      :url="viewURL"
      page-query-param="page"
      :page="parseInt($store.state.viewset.query.page)"
      :show-extra="2"
      :start="1"
      :end="Math.ceil($store.state.viewset.count / limit)"
    />

    <!-- Modal -->
    <Modal v-model="showBrandModal" size="modal-lg">
      <div v-if="brandModalBrand">
        <!-- Title -->
        <h3 class="mb-4">{{ brandModalBrand.brand_name }}</h3>

        <!-- Labels -->
        <div class="d-flex justify-content-between mb-3">
          <div class="d-flex flex-column">
            <div
              v-for="label in brandModalBrand.labels"
              :key="label"
              class="d-flex align-items-center mb-2"
            >
              <img
                class="label-icon-img me-2"
                :src="`/static/img/labels/${labelsMap[label].abbreviation}.png`"
              />
              {{ labelsMap[label].label_name }}
            </div>
          </div>
          <div class="d-flex flex-column mx-auto">
            <div
              v-for="cert in brandModalBrand.certificate"
              :key="cert.id"
              class="d-flex align-items-center mb-2"
            >
              <img class="label-icon-img me-2" :src="cert.static_url" />
              {{ cert.name }}
            </div>
          </div>
        </div>

        <!-- About brand -->
        <div v-if="modalIsLoading" class="text-center mb-4">
          <IconLoading />
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-else-if="brandModalBrand.about_brand != 'None'"
          class="admin-text admin-text-no-empty-paragraphs last-p-mb-0 mb-4"
          v-html="brandModalBrand.about_brand"
        ></div>
        <!-- eslint-enable vue/no-v-html -->

        <!-- Brand tags -->
        <p
          v-if="brandModalBrand.tags && brandModalBrand.tags.length > 0"
          class="lead mb-4 mt-n1"
        >
          <template v-for="tag in brandModalBrand.tags">
            <span
              :key="tag.id"
              class="badge bg-success me-1 cece-tooltip"
              :data-tooltip="tag.display_name + ': ' + tag.description"
            >
              #{{ tag.tag }}
            </span>
          </template>
        </p>

        <!-- Shop button -->
        <a
          v-if="brandModalBrand.has_products"
          :href="brandModalBrand.get_absolute_url"
          class="btn btn-primary btn-icon mt-1"
        >
          Shop {{ brandModalBrand.brand_name }}
          <IconChevronRight class="white ms-2" />
        </a>
      </div>
    </Modal>
  </div>
</template>

<script>
import { isEqual } from 'lodash';
import SearchInput from '../SearchInput.vue';
import Pagination from '../Pagination.vue';
import Modal from '../Modal.vue';
import BrandTile from './BrandTile.vue';
import IconChevronRight from '../../../../../templates/icons/chevron-right.svg';
import IconLoading from '../../../../../templates/icons/loading.svg';
import IconCheckmark from '../../../../../templates/icons/checkmark.svg';
import FilterDropdown from '../FilterDropdown.vue';
import { $axios } from '../../util/axios';

const addOrRemoveFromArray = (array, item) =>
  array.includes(item) ? array.filter(i => i != item) : [...array, item];

export default {
  components: {
    SearchInput,
    FilterDropdown,
    Pagination,
    Modal,
    BrandTile,
    IconChevronRight,
    IconLoading,
    IconCheckmark,
  },
  props: {
    limit: {
      type: Number,
      default: 24,
    },
    viewURL: {
      type: String,
      default: '/',
    },
    apiURL: {
      type: String,
      default: '/',
    },
  },
  async serverPrefetch() {
    await Promise.all([
      this.$store.dispatch('viewset/fetch', {
        page: this.$route.query.page ? parseInt(this.$route.query.page) : 1,
        query: this.searchQuery,
      }),
      this.$store.dispatch('labels/fetch'),
      this.$store.dispatch('certificates/fetch'),
    ]);
  },
  data() {
    return {
      showBrandModal: false,
      brandModalBrand: null,
      searchQuery: '',
      labelsMapCache: null,
      isLoading: false,
      modalIsLoading: false,
      selectedLabels: [],
      selectedCertificates: [],
    };
  },
  computed: {
    currentPageNumber() {
      return parseInt(this.$store.state.viewset.query.page);
    },
    labelsMap() {
      // Cache manually as caching is disabled during SSR, and the map
      // reduce is quite an expensive process
      if (this.labelsMapCache) {
        return this.labelsMapCache;
      }

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.labelsMapCache = this.$store.state.labels.results.reduce(
        (map, label) => {
          map[label.id] = label;
          return map;
        },
        {}
      );
      return this.labelsMapCache;
    },
  },
  watch: {
    async $route(to) {
      if (to.query.page && parseInt(to.query.page) !== this.currentPageNumber) {
        this.scrollUpwards();
        this.fetchBrandviewset({
          page: parseInt(to.query.page),
          query: this.searchQuery,
          labels: this.selectedLabels,
          certificates: this.selectedCertificates,
        });
      } else if (!to.query.page && this.currentPageNumber > 1) {
        this.scrollUpwards();
        this.fetchBrandviewset({
          page: 1,
          query: this.searchQuery,
          labels: this.selectedLabels,
          certificates: this.selectedCertificates,
        });
      }
    },
  },
  methods: {
    scrollUpwards() {
      this.$refs['search-input-component'].$el.scrollIntoView({
        behavior: 'instant',
      });
    },
    onLabelSelect(label) {
      this.selectedLabels = addOrRemoveFromArray(this.selectedLabels, label.id);

      // Fetch
      this.fetchBrandviewset({
        page: 1,
        query: this.searchQuery,
        labels: this.selectedLabels,
        certificates: this.selectedCertificates,
      });
    },
    onCertificateSelect(certificate) {
      this.selectedCertificates = addOrRemoveFromArray(
        this.selectedCertificates,
        certificate.id
      );

      // Fetch
      this.fetchBrandviewset({
        page: 1,
        query: this.searchQuery,
        labels: this.selectedLabels,
        certificates: this.selectedCertificates,
      });
    },
    onReset() {
      this.searchQuery = '';
      this.fetchBrandviewset({
        page: 1,
        query: this.searchQuery,
        labels: this.selectedLabels,
        certificates: this.selectedCertificates,
      });
    },
    async openBrandModal(brand) {
      try {
        this.brandModalBrand = brand;
        this.showBrandModal = true;
        this.modalIsLoading = true;
        const { data: result } = await $axios().get(this.apiURL + brand.id);
        this.modalIsLoading = false;
        this.brandModalBrand = result;
      } catch (err) {
        console.error('Error fetching brand detail', err);
      }
    },
    async fetchBrandviewset({ page, query, labels, certificates }) {
      // If there is no page change, don't do anything
      if (
        page == this.currentPageNumber &&
        query === this.$store.state.viewset.query.query &&
        isEqual(labels, this.$store.state.viewset.query.labels) &&
        isEqual(certificates, this.$store.state.viewset.query.certificates)
      ) {
        return;
      }

      // Don't fetch when the search query is short (and there was no previous search query)
      if (query && query.length < 2 && !this.$store.state.viewset.query.query) {
        return;
      }

      this.isLoading = true;
      await this.$store.dispatch('viewset/fetch', {
        page,
        query: query.length > 1 ? query : '',
        labels: [...labels],
        certificates: [...certificates],
      });
      this.isLoading = false;

      // If the page number didn't change stop
      const queryPage = this.$route.query.page
        ? parseInt(this.$route.query.page)
        : 1;
      if (this.currentPageNumber === queryPage) {
        return;
      }

      // Otherwise update the url
      this.$router.push(
        `/sustainable-clothing-brands/${
          this.currentPageNumber > 1 ? `?page=${this.currentPageNumber}` : ''
        }`
      );
    },
  },
};
</script>
